/**
 * The bank and the shop.
 *
 * Both are the same shape on screen: a grid of what is there, drawn beside the
 * bag, with a click moving one thing and the menu offering amounts. Neither panel
 * decides anything; every click is a request and the answer arrives as a
 * container update like any other.
 */

import type { ItemStack } from '@inverse/common';
import type { ItemDefinitionWire, ShopEntryWire } from '@inverse/protocol';
import { useRef, useState } from 'react';
import { useDraggableWindow } from './draggable';
import { useLongPress } from './longPress';
import { CloseButton } from './CloseButton';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';

function initials(name: string): string {
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '').slice(0, 1)}${(words[1] ?? '').slice(0, 1)}`.toUpperCase();
}

function tint(itemId: number): string {
  return `hsl(${(itemId * 47) % 360} 38% 38%)`;
}

/** Long counts read better short, the way a balance does. */
function shortQuantity(quantity: number): string {
  if (quantity >= 10_000_000) return `${Math.floor(quantity / 1_000_000)}M`;
  if (quantity >= 100_000) return `${Math.floor(quantity / 1000)}K`;
  return String(quantity);
}

/** The tabs a bank sorts by: everything, then a page per kind of thing. */
const BANK_TABS: readonly { kind: string; label: string; title: string }[] = [
  { kind: 'all', label: '∞', title: 'Everything' },
  { kind: 'weapon', label: 'Weapons', title: 'Weapons' },
  { kind: 'armour', label: 'Armour', title: 'Armour' },
  { kind: 'ammunition', label: 'Ammo', title: 'Arrows, dust and anything else that is spent' },
  { kind: 'consumable', label: 'Food', title: 'Consumables' },
  { kind: 'material', label: 'Materials', title: 'Materials' },
  { kind: 'quest', label: 'Quest', title: 'Quest items' },
  { kind: 'other', label: 'Other', title: 'Everything else' },
];

export function Bank({
  slots,
  size,
  describe,
  onWithdraw,
  onHover,
  onMenu,
  onDepositBag,
  onDepositWorn,
  onClose,
}: {
  slots: readonly (ItemStack | null)[];
  /** How many different things it can hold, which is the thing worth knowing. */
  size: number;
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onWithdraw: (slot: number) => void;
  /** What the pointer is resting on, so a tooltip can say what it is. */
  onHover?: (itemId: number | null) => void;
  onMenu: (slot: number, screenX: number, screenY: number) => void;
  /** Everything in the bag, and everything being worn, straight in. */
  onDepositBag: () => void;
  onDepositWorn: () => void;
  onClose: () => void;
}): JSX.Element {
  const pressed = useRef(-1);
  const hold = useLongPress((x, y) => {
    if (pressed.current >= 0) onMenu(pressed.current, x, y);
  });

  const { attach, style, onPointerDown, nudge } = useDraggableWindow('bank');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('bank', nudge, altHeld, {
    width: 320,
    height: 240,
  });
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');

  const held = slots.filter((slot) => slot !== null);
  const wanted = search.trim().toLowerCase();

  // What this page is showing: the tab narrows it, and the search narrows that.
  const showing = slots
    .map((stack, slot) => ({ stack, slot }))
    .filter(({ stack }) => {
      if (!stack) return false;

      const definition = describe(stack.id);
      if (tab !== 'all' && (definition?.kind ?? 'material') !== tab) return false;
      if (!wanted) return true;

      return (definition?.name ?? '').toLowerCase().includes(wanted);
    });

  return (
    <div
      className={`trade bank${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Bank</span>
        <span className="panel-count">
          {held.length}/{size}
        </span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="bank-tabs">
        {BANK_TABS.map((entry) => (
          <button
            key={entry.kind}
            type="button"
            className={tab === entry.kind ? 'bank-tab on' : 'bank-tab'}
            title={entry.title}
            onClick={() => setTab(entry.kind)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="bank-tools">
        <input
          className="editor-search"
          type="text"
          value={search}
          placeholder="Find"
          onChange={(event) => setSearch(event.target.value)}
        />
        <button type="button" title="Everything in your bag goes in" onClick={onDepositBag}>
          Bag
        </button>
        <button type="button" title="Everything you are wearing goes in" onClick={onDepositWorn}>
          Worn
        </button>
      </div>

      {held.length === 0 ? (
        <p className="hint">Your bank is empty. Put something in from your bag.</p>
      ) : (
        <div className="trade-grid bank-grid">
          {showing.map(({ stack, slot }) => {
            if (!stack) return null;
            const name = describe(stack.id)?.name ?? `Item ${stack.id}`;

            return (
              <div
                key={slot}
                className="slot filled"
                data-name={name}
                onPointerEnter={() => onHover?.(stack.id)}
                onPointerLeave={() => onHover?.(null)}
                onPointerDown={(event) => {
                  pressed.current = slot;
                  hold.onPointerDown(event);
                }}
                onPointerMove={hold.onPointerMove}
                onPointerUp={hold.onPointerUp}
                onPointerCancel={hold.onPointerCancel}
                onClick={() => {
                  if (!hold.consumed()) onWithdraw(slot);
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  onMenu(slot, event.clientX, event.clientY);
                }}
              >
                <span className="slot-icon" style={{ background: tint(stack.id) }}>
                  {initials(name)}
                </span>
                <span className="slot-quantity">{shortQuantity(stack.quantity)}</span>
              </div>
            );
          })}

          {showing.length === 0 ? <p className="hint">Nothing here matches.</p> : null}
        </div>
      )}
      {grips}
    </div>
  );
}

export function Shop({
  name,
  entries,
  describe,
  onBuy,
  onClose,
}: {
  name: string;
  entries: readonly ShopEntryWire[];
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onBuy: (slot: number, quantity: number) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('shop');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('shop', nudge, altHeld, {
    width: 280,
    height: 220,
  });

  return (
    <div
      className={`trade${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>{name}</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="shop-list">
        {entries.map((entry, slot) => {
          const itemName = describe(entry.itemId)?.name ?? `Item ${entry.itemId}`;

          return (
            <div
              key={entry.itemId}
              className={entry.locked ? 'shop-row locked' : 'shop-row'}
              title={entry.locked ? 'Not for sale to you yet' : undefined}
            >
              <span className="slot-icon small" style={{ background: tint(entry.itemId) }}>
                {initials(itemName)}
              </span>
              <span className="shop-name">
                {itemName}
                {/* Shown rather than hidden: a shelf somebody could work
                    towards is worth seeing. */}
                <span className="shop-stock">
                  {entry.locked ? 'Not for you yet' : `${entry.quantity} in stock`}
                </span>
              </span>
              <span className="shop-price">{entry.price}</span>
              <span className="shop-buttons">
                <button
                  type="button"
                  disabled={entry.locked || entry.quantity === 0}
                  onClick={() => onBuy(slot, 1)}
                >
                  Buy
                </button>
                <button
                  type="button"
                  disabled={entry.locked || entry.quantity === 0}
                  onClick={() => onBuy(slot, 5)}
                >
                  ×5
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <p className="hint">Click something in your bag to sell it here.</p>
      {grips}
    </div>
  );
}
